/**
 * Startbelegung / Rotations-Anker (G-17): erfragt beim ersten Start eines
 * Schichtplans, welches Team aktuell in welcher Schicht arbeitet, damit die
 * Rotation phasenrichtig (bruchlos zum real gelebten Stand) fortgesetzt wird.
 *
 * Matrix je Schicht (Zeilen nach Rotationsreihenfolge, z. B. Früh/Spät/Nacht)
 * → Team-Select. Vorbefüllt mit der aktuellen Belegung (falls Anker schon
 * gesetzt) bzw. der „natürlichen“ Phase (Schicht[i] → Team[i]). Jahr/KW =
 * Vorschlags-KW. Speichern → setRotationAnchor; 422 = keine gültige
 * Rotationsphase, 409 = es existieren bereits Schichtpläne.
 *
 * Gate: personal_schreiben (durchgesetzt wird serverseitig).
 */

import { useEffect, useMemo, useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { AlertCircle } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SHEET_WIDTH,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import { weeksInIsoYear } from "@/lib/isoWeek"
import { api, apiError, apiStatus } from "@/services/api"
import type { RotationAnchorResponse, RotationStatus } from "@/types"

interface StartAssignmentSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Numerische Id des Schichtmodells (aus dem Generator-Flow). */
  modelId: number
  modelName?: string
  /** Vorschalt-Status (Teams/Schichten, Vorschlags-KW, ggf. aktuelle Belegung). */
  status: RotationStatus
  /** Wird nach erfolgreichem Setzen des Ankers aufgerufen (z. B. Generierung fortsetzen). */
  onAnchorSet?: (result: RotationAnchorResponse) => void
}

export function StartAssignmentSheet({
  open,
  onOpenChange,
  modelId,
  modelName,
  status,
  onAnchorSet,
}: StartAssignmentSheetProps) {
  const queryClient = useQueryClient()

  // Schichten nach Rotationsreihenfolge (Früh/Spät/Nacht), Teams nach order.
  const shifts = useMemo(
    () => [...status.shifts].sort((a, b) => a.order - b.order),
    [status.shifts]
  )
  const teams = useMemo(() => [...status.teams].sort((a, b) => a.order - b.order), [status.teams])

  const suggested = status.suggestedAnchorWeek
  const [year, setYear] = useState(String(suggested?.year ?? new Date().getFullYear()))
  const [week, setWeek] = useState(String(suggested?.week ?? 1))
  // Belegung als {shiftId → teamId} (leere Auswahl möglich → "")
  const [assignment, setAssignment] = useState<Record<string, string>>({})

  // Vorbefüllung: aktuelle Belegung (falls vorhanden), sonst natürliche Phase.
  useEffect(() => {
    if (!open) return
    setYear(String(suggested?.year ?? new Date().getFullYear()))
    setWeek(String(suggested?.week ?? 1))

    const initial: Record<string, string> = {}
    if (status.currentAssignment && status.currentAssignment.length > 0) {
      for (const a of status.currentAssignment) initial[a.shiftId] = a.teamId
    } else {
      // Natürliche Phase: Schicht[i] → Team[i] (zyklisch, falls ungleich viele).
      shifts.forEach((s, i) => {
        const team = teams[i % teams.length]
        if (team) initial[s.id] = team.id
      })
    }
    setAssignment(initial)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, status, shifts, teams])

  const maxWeek = weeksInIsoYear(Number(year) || new Date().getFullYear())

  // Live-Validierung: jede Schicht braucht ein Team, kein Team doppelt.
  const chosen = shifts.map((s) => assignment[s.id] ?? "")
  const missing = chosen.some((t) => t === "")
  const usedTeams = chosen.filter((t) => t !== "")
  const duplicate = new Set(usedTeams).size !== usedTeams.length
  const weekValid = Number(year) >= 2000 && Number(week) >= 1 && Number(week) <= maxWeek
  const canSave = !missing && !duplicate && weekValid

  const liveHint = missing
    ? "Bitte für jede Schicht ein Team wählen."
    : duplicate
      ? "Jedes Team darf nur in genau einer Schicht stehen — aktuell ist ein Team doppelt gewählt."
      : null

  const setTeam = (shiftId: string, teamId: string) =>
    setAssignment((prev) => ({ ...prev, [shiftId]: teamId }))

  const save = useMutation({
    mutationFn: () =>
      api.planning.rotation.setAnchor(modelId, {
        year: Number(year),
        week: Number(week),
        assignment: shifts.map((s) => ({
          shiftId: Number(s.id),
          teamId: Number(assignment[s.id]),
        })),
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["rotationStatus", modelId] })
      onAnchorSet?.(result)
      onOpenChange(false)
    },
  })

  // Serverfehler verständlich aufbereiten (422 vs. 409 vs. sonstiges).
  const serverError = (() => {
    if (!save.isError) return null
    const s = apiStatus(save.error)
    if (s === 422)
      return "Diese Belegung ist keine gültige Rotationsphase — jedes Team muss in genau einer Schicht stehen und der Reihe nach verschoben sein."
    if (s === 409)
      return "Es existieren bereits Schichtpläne; der Rotationsstart ist damit festgelegt und kann nicht mehr geändert werden."
    return apiError(save.error)
  })()

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent sheetKey="start-assignment" defaultWidth={SHEET_WIDTH.lg} className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Schichtplan starten — Startbelegung{modelName ? ` (${modelName})` : ""}</SheetTitle>
          <SheetDescription>
            Dieser Schichtplan wird zum ersten Mal gestartet. Bitte geben Sie an, welches
            Team aktuell in welcher Schicht arbeitet — daraus wird der Rotationsstart
            berechnet, damit der Plan bruchlos an den real gelebten Stand anschließt.
          </SheetDescription>
        </SheetHeader>

        <div className="grid gap-4 py-2">
          {/* Anker-KW */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Jahr *</Label>
              <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Kalenderwoche *</Label>
              <Input
                type="number"
                min={1}
                max={maxWeek}
                value={week}
                onChange={(e) => setWeek(e.target.value)}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Für welche Kalenderwoche gilt diese Belegung? = die aktuelle bzw. erste Woche,
            ab der der Plan gilt. Vorgeschlagen ist die laufende KW.
          </p>

          {/* Matrix Schicht → Team */}
          <div className="space-y-2">
            <Label>Aktuelle Belegung je Schicht *</Label>
            <div className="space-y-2 rounded-md border p-3">
              {shifts.map((s) => (
                <div key={s.id} className="grid grid-cols-[7rem_1fr] items-center gap-3">
                  <span className="text-sm font-medium">{s.name}</span>
                  <Select
                    value={assignment[s.id] ?? ""}
                    onChange={(e) => setTeam(s.id, e.target.value)}
                  >
                    <option value="">— Team wählen —</option>
                    {teams.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </Select>
                </div>
              ))}
              {shifts.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Dieses Schichtmodell hat keine Schichten.
                </p>
              )}
            </div>
          </div>

          {/* Live-Hinweis */}
          {liveHint && (
            <p className="flex items-start gap-1.5 text-xs text-amber-600 dark:text-amber-500">
              <AlertCircle className="mt-0.5 size-3.5 shrink-0" />
              {liveHint}
            </p>
          )}

          {/* Serverfehler */}
          {serverError && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
              <AlertCircle className="mt-0.5 size-4 shrink-0" />
              <span>{serverError}</span>
            </div>
          )}
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button disabled={!canSave || save.isPending} onClick={() => save.mutate()}>
            Startbelegung speichern
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
